import { useHandler } from "@redotech/react-util/hook";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

export const QuantityIncrementDecrement = memo(
  function QuantityIncrementDecrement({
    value,
    setValue,
    max,
    min,
    title,
    subtitle,
    compact = false,
    size = "lg",
    disabled = false,
    numberColor = "#FF4405",
  }: {
    value: number;
    setValue(value: number): void;
    max?: number;
    min: number;
    title?: string;
    subtitle?: string;
    compact?: boolean;
    size?: "lg" | "sm";
    disabled?: boolean;
    numberColor?: string;
  }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value.toString());
    const inputRef = useRef<HTMLInputElement>(null);

    const handleNumberClick = useHandler(() => {
      setIsEditing(true);
      setEditValue(value.toString());
    });

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isEditing]);

    const hasMax = max !== undefined;

    const handleIncrement = useHandler(() => {
      if (hasMax) {
        setValue(Math.min(value + 1, max));
        return;
      }

      setValue(value + 1);
    });

    const handleDecrement = useHandler(() => {
      setValue(Math.max(value - 1, min));
    });

    const handleEditSubmit = useHandler(() => {
      const newValue = parseInt(editValue, 10);
      let withinMax = true;
      if (hasMax) {
        withinMax = newValue <= max;
      }
      if (!isNaN(newValue) && newValue >= min && withinMax) {
        setValue(newValue);
      }
      setIsEditing(false);
    });

    const handleBlur = useHandler((_e: React.FocusEvent<HTMLInputElement>) => {
      // flushSync forces React to commit the state update before returning,
      // so parent Confirm button clicks read the updated value instead of stale state.
      flushSync(() => {
        handleEditSubmit();
      });
    });

    const handleEditCancel = useHandler(() => {
      setIsEditing(false);
      setEditValue(value.toString());
    });

    const handleEditKeyDown = useHandler((e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleEditSubmit();
      } else if (e.key === "Escape") {
        handleEditCancel();
      }
    });

    const isSmall = size === "sm";

    return (
      <Flex
        borderRadius="lg"
        p={isSmall ? "none" : compact ? "md" : "3xl"}
        w={isSmall ? "fit-content" : "full"}
      >
        <Flex dir="column" gap={compact ? "sm" : "md"} w="full">
          {title && (
            <Text fontSize={compact ? "sm" : "md"} textAlign="center">
              {title}
            </Text>
          )}
          <Flex align="center" justify="center" w="full">
            <Flex align="center" gap={isSmall ? "xs" : "md"}>
              {(!isEditing || isSmall) && (
                <RedoButton
                  disabled={disabled || value <= min}
                  hierarchy="secondary"
                  IconLeading={isSmall ? undefined : MinusIcon}
                  onClick={handleDecrement}
                  size={isSmall ? "sm" : "lg"}
                  style={
                    isSmall
                      ? undefined
                      : {
                          borderRadius: "var(--border-radius-full)",
                          width: "56px",
                          height: "56px",
                          minWidth: "56px",
                          padding: "0",
                        }
                  }
                  text={isSmall ? "-" : undefined}
                />
              )}

              <Flex
                align="center"
                justify="center"
                onClick={!isEditing ? handleNumberClick : undefined}
                px={isSmall ? "none" : "xl"}
                style={{
                  cursor: isEditing ? "text" : "pointer",
                  minWidth: isSmall ? "40px" : undefined,
                }}
              >
                {isEditing ? (
                  <Flex style={{ width: isSmall ? 56 : 96 }}>
                    <RedoTextInput
                      inputMode="numeric"
                      inputTypeHint="text"
                      onBlur={handleBlur}
                      onKeyDown={handleEditKeyDown}
                      ref={inputRef}
                      setValue={setEditValue}
                      size={isSmall ? "sm" : "lg"}
                      value={editValue}
                    />
                  </Flex>
                ) : (
                  <Text
                    fontSize={isSmall ? "xl" : "2xl"}
                    fontWeight={isSmall ? "semibold" : "bold"}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor =
                        "var(--redo-colors-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                    style={
                      isSmall
                        ? undefined
                        : {
                            lineHeight: compact ? "56px" : "88px",
                            fontSize: compact ? "48px" : "72px",
                            color: numberColor,
                          }
                    }
                    textAlign="center"
                  >
                    {value}
                  </Text>
                )}
              </Flex>
              {(!isEditing || isSmall) && (
                <RedoButton
                  disabled={disabled || (hasMax && value >= max)}
                  hierarchy="secondary"
                  IconLeading={isSmall ? undefined : PlusIcon}
                  onClick={handleIncrement}
                  size={isSmall ? "sm" : "lg"}
                  style={
                    isSmall
                      ? undefined
                      : {
                          borderRadius: "var(--border-radius-full)",
                          width: "56px",
                          height: "56px",
                          minWidth: "56px",
                          padding: 0,
                        }
                  }
                  text={isSmall ? "+" : undefined}
                />
              )}
            </Flex>
          </Flex>
          {subtitle && (
            <Text
              fontSize="xs"
              style={{ whiteSpace: "pre-line" }}
              textAlign="center"
              textColor="disabled"
            >
              {subtitle}
            </Text>
          )}
        </Flex>
      </Flex>
    );
  },
);
